import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { recordService } from "@/integrations/records/recordService";
import { RefreshCw, FileCode } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Template {
  id: string;
  name: string;
  description: string;
  prompt: string;
  isSystem: boolean | null;
  userId: string | null;
  createdAt: string;
  orderCode?: number | null;
  lang?: string;
}

interface ReprocessDialogProps {
  open: boolean;
  onClose: () => void;
  recordId: string;
  recordName: string;
  currentTemplateId?: string;
  onSuccess?: (processedText: string) => void;
  onRefetchNeeded?: () => void;
}

export const ReprocessDialog = ({
  open,
  onClose,
  recordId,
  recordName,
  currentTemplateId,
  onSuccess,
  onRefetchNeeded,
}: ReprocessDialogProps) => {
  const { t, i18n } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(currentTemplateId || "");
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchTemplates = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const currentLang = i18n.language.split('-')[0];
      const { data, error } = await supabase
        .from("templates")
        .select("*")
        .or(`user_id.eq.${user.id},and(is_system.eq.true,lang.eq.${currentLang})`)
        .order("order_code", { ascending: true, nullsFirst: false })
        .order("is_system", { ascending: false })
        .order("name");

      if (error) throw error;

      const mappedTemplates: Template[] = data.map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        prompt: template.prompt,
        isSystem: template.is_system,
        userId: template.user_id,
        createdAt: template.created_at,
        orderCode: template.order_code,
        lang: template.lang,
      }));

      setTemplates(mappedTemplates);
    } catch (error) {
      console.error("Ошибка загрузки шаблонов:", error);
      toast({
        title: t('records.dialogs.reprocess.load_error_title'),
        description: t('records.dialogs.reprocess.load_error_desc'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast, t]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSelectedTemplateId(currentTemplateId || "");
    }
  }, [open, currentTemplateId, fetchTemplates]);

  const handleReprocess = async () => {
    if (!selectedTemplateId) {
      toast({
        title: t('records.dialogs.reprocess.select_error_title'),
        description: t('records.dialogs.reprocess.select_error_desc'),
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(true);
      const response = await recordService.reprocessRecord(recordId, selectedTemplateId);

      // Инвалидируем кэш для обновления данных на странице
      queryClient.invalidateQueries({
        queryKey: ["record", recordId],
        exact: true,
      });

      // Инвалидируем также кэш записей на главной странице
      queryClient.invalidateQueries({
        queryKey: ["records"],
      });

      toast({
        title: t('records.dialogs.reprocess.success_title'),
        description: response.message || t('records.dialogs.reprocess.success_desc'),
      });

      // Вызываем функцию перезагрузки данных
      onRefetchNeeded?.();

      onSuccess?.(response.processed_text);
      onClose();
    } catch (error) {
      console.error("Ошибка повторной обработки:", error);
      toast({
        title: t('records.dialogs.reprocess.process_error_title'),
        description: error instanceof Error ? error.message : t('records.dialogs.reprocess.process_error_desc'),
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-sm:h-full max-sm:max-w-full max-sm:rounded-none max-sm:m-0 max-sm:border-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={20} className="text-brand-500" />
            {t('records.dialogs.reprocess.title')}
          </DialogTitle>
          <DialogDescription>
            {t('records.dialogs.reprocess.description', { name: recordName })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner message={t('records.dialogs.reprocess.loading_templates')} />
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] max-sm:max-h-[60vh] overflow-y-auto">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${selectedTemplateId === template.id
                    ? "border-brand-300 bg-brand-50/70 dark:border-brand-400 dark:bg-brand-950/50"
                    : "border-border hover:border-brand-200 hover:bg-muted"
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <FileCode
                      size={18}
                      className={`mt-0.5 flex-shrink-0 ${template.isSystem ? "text-blue-500" : "text-brand-500"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm text-foreground truncate">{template.name}</h4>
                        {template.isSystem && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                            {t('records.dialogs.reprocess.system_label')}
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {templates.length === 0 && !loading && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileCode size={24} className="mx-auto mb-2 text-muted-foreground" />
                  <p>{t('records.dialogs.reprocess.no_templates')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={processing}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleReprocess} disabled={!selectedTemplateId || processing || loading}>
            {processing ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                {t('records.dialogs.reprocess.processing')}
              </>
            ) : (
              <>
                <RefreshCw size={16} className="mr-2" />
                {t('records.dialogs.reprocess.process_button')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
